import React, { useState, useEffect } from 'react';
import { ClientLink, PatientLink } from '../components/EntityLinks';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import apiClient from '../../../api/api';
import { formatMobileNumber } from '../../../utils/phoneFormat';
import { 
  Users,
  Filter,
  Search,
  UserCheck,
  UserX,
  Home,
  User,
  Clock,
  MapPin,
  Phone,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Mail,
  Send,
  Check,
  Loader2
} from 'lucide-react';

const StaffRoster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [assigningStaff, setAssigningStaff] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [assignmentStep, setAssignmentStep] = useState(1); // 1: Select Agent, 2: Upload Payment, 3: Confirm
  const [selectedStaffForAssignment, setSelectedStaffForAssignment] = useState(null);
  const [paymentSlipFile, setPaymentSlipFile] = useState(null);
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [bookingCompleted, setBookingCompleted] = useState(false);
  const [availableStaffForAssignment, setAvailableStaffForAssignment] = useState([]);
  const [useSmartFiltering, setUseSmartFiltering] = useState(true);
  
  // Smart Filters (when enabled)
  const [statusFilter, setStatusFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [liveInFilter, setLiveInFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Admin Filters (when smart filters disabled)
  const [adminSearchTerm, setAdminSearchTerm] = useState('');
  const [adminRoleFilter, setAdminRoleFilter] = useState('all');
  const [adminLiveInFilter, setAdminLiveInFilter] = useState('all');
  
  // Get service request data from location state
  const serviceRequest = location.state?.serviceRequest;

  // Candidate profiles already sent to the client for this service request (one-time per staff)
  const [sentCandidateIds, setSentCandidateIds] = useState(new Set());
  const [sendingCandidateId, setSendingCandidateId] = useState(null);
  const [candidateNotice, setCandidateNotice] = useState(null); // { type: 'success' | 'error', text }

  useEffect(() => {
    fetchStaff();
  }, [statusFilter, genderFilter, liveInFilter, roleFilter, adminSearchTerm, adminRoleFilter, adminLiveInFilter, useSmartFiltering]);

  useEffect(() => {
    const requestId = serviceRequest?.request_id;
    if (!requestId) return;
    (async () => {
      try {
        const res = await apiClient.getSentCandidates(requestId);
        setSentCandidateIds(new Set((res.data || []).map((r) => r.staff_profile_id)));
      } catch (err) {
        console.error('Failed to load sent candidates:', err);
      }
    })();
  }, [serviceRequest?.request_id]);

  const handleSendCandidate = async (staffMember) => {
    if (!serviceRequest?.request_id) return;
    setSendingCandidateId(staffMember.staff_profile_id);
    setCandidateNotice(null);
    try {
      await apiClient.sendCandidateProfile(serviceRequest.request_id, staffMember.staff_profile_id);
      setSentCandidateIds((prev) => new Set(prev).add(staffMember.staff_profile_id));
      setCandidateNotice({ type: 'success', text: `${staffMember.full_name}'s profile sent to ${serviceRequest.payer_name || 'the client'}.` });
    } catch (err) {
      // If it was already sent (409), still mark it as sent so the UI is consistent.
      if (err?.status === 409 || /already been sent/i.test(err?.message || '')) {
        setSentCandidateIds((prev) => new Set(prev).add(staffMember.staff_profile_id));
      }
      setCandidateNotice({ type: 'error', text: err?.message || 'Failed to send candidate profile.' });
    } finally {
      setSendingCandidateId(null);
    }
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      
      let response;
      
      // Always start with all staff and apply filters
      response = await apiClient.getAllStaff();
      let allStaff = response.data || [];
      
      // Debug: Log role data structure
      console.log('Role data structure sample:', allStaff[0]?.role);
      console.log('All unique roles in database:', [...new Set(allStaff.map(staff => staff.role))]);
      
      // Apply filters client-side for better control
      let filteredStaff = allStaff;
      
      if (useSmartFiltering) {
        // Smart Filters (when enabled) - Show all staff with manual filtering
        // Status filter
        if (statusFilter !== 'all') {
          filteredStaff = filteredStaff.filter(staff => staff.current_status === statusFilter);
        }
        
        // Gender filter
        if (genderFilter !== 'all') {
          filteredStaff = filteredStaff.filter(staff => staff.gender === genderFilter);
        }
        
        // Live-in filter
        if (liveInFilter !== 'all') {
          if (liveInFilter === 'yes') {
            filteredStaff = filteredStaff.filter(staff => staff.willing_to_live_in === true);
          } else {
            filteredStaff = filteredStaff.filter(staff => staff.willing_to_live_in === false);
          }
        }
        
        // Role filter
        if (roleFilter !== 'all') {
          filteredStaff = filteredStaff.filter(staff => {
            // Handle different role data structures
            if (Array.isArray(staff.role)) {
              return staff.role.includes(roleFilter);
            } else if (typeof staff.role === 'string') {
              // Remove curly braces and quotes, then split by comma if multiple roles
              const cleanRole = staff.role.replace(/[{}"]/g, '');
              const roles = cleanRole.includes(',') ? cleanRole.split(',') : [cleanRole];
              return roles.includes(roleFilter);
            } else if (staff.role && typeof staff.role === 'object') {
              // Handle PostgreSQL array format: {role: ["NURSE", "CARETAKER"]}
              const roles = Object.values(staff.role);
              return roles.includes(roleFilter);
            }
            return false;
          });
        }
      } else {
        // Admin Filters (when smart filters disabled)
        
        // Search across all staff_profiles columns
        if (adminSearchTerm) {
          const searchLower = adminSearchTerm.toLowerCase();
          filteredStaff = filteredStaff.filter(staff => {
            // Search across all relevant staff_profiles columns
            return (
              staff.full_name?.toLowerCase().includes(searchLower) ||
              staff.staff_code?.toLowerCase().includes(searchLower) ||
              staff.designation?.toLowerCase().includes(searchLower) ||
              staff.home_address?.toLowerCase().includes(searchLower) ||
              staff.location?.toLowerCase().includes(searchLower) ||
              staff.email?.toLowerCase().includes(searchLower) ||
              staff.mobile_number?.includes(searchLower) ||
              staff.qualifications?.toLowerCase().includes(searchLower) ||
              staff.current_status?.toLowerCase().includes(searchLower) ||
              staff.verification_status?.toLowerCase().includes(searchLower) ||
              // Also search in roles
              (Array.isArray(staff.role) && staff.role.some(role => role.toLowerCase().includes(searchLower))) ||
              (typeof staff.role === 'string' && staff.role.toLowerCase().includes(searchLower))
            );
          });
        }
        
        // Role filter (admin)
        if (adminRoleFilter !== 'all') {
          filteredStaff = filteredStaff.filter(staff => {
            // Handle different role data structures
            if (Array.isArray(staff.role)) {
              return staff.role.includes(adminRoleFilter);
            } else if (typeof staff.role === 'string') {
              // Remove curly braces and quotes, then split by comma if multiple roles
              const cleanRole = staff.role.replace(/[{}"]/g, '');
              const roles = cleanRole.includes(',') ? cleanRole.split(',') : [cleanRole];
              return roles.includes(adminRoleFilter);
            } else if (staff.role && typeof staff.role === 'object') {
              // Handle PostgreSQL array format: {role: ["NURSE", "CARETAKER"]}
              const roles = Object.values(staff.role);
              return roles.includes(adminRoleFilter);
            }
            return false;
          });
        }
        
        // Willing to live in filter (admin)
        if (adminLiveInFilter !== 'all') {
          if (adminLiveInFilter === 'yes') {
            filteredStaff = filteredStaff.filter(staff => staff.willing_to_live_in === true);
          } else {
            filteredStaff = filteredStaff.filter(staff => staff.willing_to_live_in === false);
          }
        }
      }
      
      console.log('Filtered staff data:', filteredStaff);
      setStaff(filteredStaff);
    } catch (err) {
      console.error('Staff fetch error:', err);
      setError(`Failed to fetch staff: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'ASSIGNED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'UNAVAILABLE':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return <UserCheck className="w-4 h-4" />;
      case 'ASSIGNED':
        return <Clock className="w-4 h-4" />;
      case 'UNAVAILABLE':
        return <UserX className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const filteredStaff = staff.filter(staffMember => {
    // Use appropriate search term based on filtering mode
    const currentSearchTerm = useSmartFiltering ? searchTerm : adminSearchTerm;
    
    const matchesSearch = !currentSearchTerm || (
      staffMember.full_name?.toLowerCase().includes(currentSearchTerm.toLowerCase()) ||
      staffMember.staff_code?.toLowerCase().includes(currentSearchTerm.toLowerCase()) ||
      staffMember.email?.toLowerCase().includes(currentSearchTerm.toLowerCase()) ||
      staffMember.mobile_number?.includes(currentSearchTerm) ||
      staffMember.home_address?.toLowerCase().includes(currentSearchTerm.toLowerCase())
    );
    
    return matchesSearch;
  });

  const handleAssignStaff = async (staffMember) => {
    if (!serviceRequest) {
      setError('No service request selected for assignment');
      return;
    }

    // Show all available staff for manual selection
    let matchingStaff = staff.filter(s => s.current_status === 'AVAILABLE');

    setAvailableStaffForAssignment(matchingStaff);
    setShowAssignModal(true);
  };

  const handleConfirmAssignment = (staffMember) => {
    setSelectedStaffForAssignment(staffMember);
    setAssignmentStep(2); // Move to step 2: Upload Payment
  };

  const handlePaymentNext = () => {
    if (!paymentSlipFile) {
      setError('Please upload a payment slip');
      return;
    }
    setAssignmentStep(3); // Move to step 3: Confirm
  };

  const handlePaymentSubmit = async () => {
    try {
      setUploadingPayment(true);
      
      // First, try to get quote for this service request
      let quoteId = serviceRequest.quote_id;
      
      if (!quoteId) {
        // Try to fetch quote for this service request
        try {
          const quotesResponse = await apiClient.getServiceRequestQuotes(serviceRequest.request_id);
          if (quotesResponse.data && quotesResponse.data.length > 0) {
            quoteId = quotesResponse.data[0].quote_id;
            console.log('Found existing quote:', quoteId);
          }
        } catch (quoteErr) {
          console.log('Could not fetch quotes:', quoteErr);
        }
      }
      
      const bookingData = {
        request_id: serviceRequest.request_id,
        assigned_staff_id: selectedStaffForAssignment.staff_profile_id,
        quote_id: quoteId || null
      };
      
      console.log('Sending booking data:', bookingData);
      console.log('Payment slip file:', paymentSlipFile);
      
      // Call convertToBooking API with file upload
      await apiClient.convertToBooking(bookingData, paymentSlipFile);
      
      // Show success message
      setBookingCompleted(true);
      
      // Navigate back to service requests after a delay
      setTimeout(() => {
        navigate('/admin/service-requests', { 
          state: { 
            message: 'Staff assigned successfully and booking created!',
            type: 'success'
          }
        });
      }, 3000); // Wait 3 seconds to show success message
    } catch (err) {
      console.error('Assignment error:', err);
      setError(`Failed to assign staff: ${err.message || 'Unknown error'}`);
    } finally {
      setUploadingPayment(false);
    }
  };

  const handleStepBack = () => {
    if (assignmentStep > 1) {
      setAssignmentStep(assignmentStep - 1);
    }
  };

  const resetAssignmentFlow = () => {
    setShowAssignModal(false);
    setAssignmentStep(1);
    setSelectedStaffForAssignment(null);
    setPaymentSlipFile(null);
    setError(null);
    setBookingCompleted(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Staff Roster" subtitle="Loading...">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Staff Roster" subtitle="Error occurred">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Staff Roster" 
      subtitle={`${filteredStaff.length} staff members available`}
    >
      {/* Service Request Info */}
      {serviceRequest && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Assigning Staff for Service Request</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-800">Patient: <PatientLink id={serviceRequest.patient_id}>{serviceRequest.patient_name}</PatientLink></span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-800">Contact: {formatMobileNumber(serviceRequest.payer_mobile)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-800">Location: {serviceRequest.location_address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-800">Start: {new Date(serviceRequest.start_date).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-800">Service: {serviceRequest.service_type}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-800">Age: {serviceRequest.patient_age}</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-800">Condition: {serviceRequest.patient_condition}</span>
                </div>
              </div>
              {serviceRequest.preferred_gender && serviceRequest.preferred_gender !== 'ANY' && (
                <div className="mt-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-800">Preferred Gender: {serviceRequest.preferred_gender}</span>
                </div>
              )}
              {serviceRequest.remarks && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-blue-800 mb-1">Remarks:</p>
                  <p className="text-sm text-blue-700">{serviceRequest.remarks}</p>
                </div>
              )}
              
              {/* Assign Button */}
              <div className="flex justify-end pt-4 border-t border-slate-200">
                <button
                  onClick={() => handleAssignStaff()}
                  disabled={loading || assigningStaff}
                  className="px-6 py-2 font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assigningStaff ? 'Processing...' : 'Assign Staff'}
                </button>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/service-requests')}
              className="px-4 py-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
        {/* Smart Filtering Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filter Mode:</span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useSmartFiltering}
                onChange={(e) => setUseSmartFiltering(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-slate-700">
                {useSmartFiltering ? 'Smart Filtering' : 'Admin Filters'}
              </span>
            </label>
          </div>
          <div className="text-xs text-slate-500">
            {useSmartFiltering 
              ? 'Manual filtering with service request context visible' 
              : 'Manual filtering across all staff profiles'
            }
          </div>
        </div>

        {useSmartFiltering ? (
          /* Smart Filters */
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, phone, or address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">Smart Filters:</span>
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="AVAILABLE">Available</option>
                <option value="ASSIGNED">Assigned</option>
                <option value="UNAVAILABLE">Unavailable</option>
              </select>
              
              <select
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Genders</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
              </select>
              
              <select
                value={liveInFilter}
                onChange={(e) => setLiveInFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Live-in Preference</option>
                <option value="yes">Willing to Live-in</option>
                <option value="no">Not Willing to Live-in</option>
              </select>
              
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Roles</option>
                <option value="NURSE">Nurse</option>
                <option value="CARETAKER">Caregiver</option>
                <option value="NANNY">Nanny</option>
              </select>
            </div>
          </div>
        ) : (
          /* Admin Filters */
          <div className="space-y-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search across all staff profiles (name, email, phone, address, designation, qualifications, status, etc.)..."
                  value={adminSearchTerm}
                  onChange={(e) => setAdminSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">Admin Filters:</span>
              </div>
              
              <select
                value={adminRoleFilter}
                onChange={(e) => setAdminRoleFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Roles</option>
                <option value="NURSE">Nurse</option>
                <option value="CARETAKER">Caregiver</option>
                <option value="NANNY">Nanny</option>
                <option value="STAFF">Staff</option>
                <option value="SUPER_ADMIN">Super Admin</option>
                <option value="ACCOUNTS">Accounts</option>
                <option value="COORDINATOR">Coordinator</option>
                <option value="SALES">Sales</option>
                <option value="STORE_MANAGER">Store Manager</option>
              </select>
              
              <select
                value={adminLiveInFilter}
                onChange={(e) => setAdminLiveInFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Live-in Preference</option>
                <option value="yes">Willing to Live-in</option>
                <option value="no">Not Willing to Live-in</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Candidate send notice */}
      {candidateNotice && (
        <div className={`mb-4 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${
          candidateNotice.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {candidateNotice.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          <span className="flex-1">{candidateNotice.text}</span>
          <button onClick={() => setCandidateNotice(null)} className="opacity-60 hover:opacity-100">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Staff List */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {filteredStaff.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No staff members found matching your criteria</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Staff Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Gender
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Live-in
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredStaff.map((staffMember) => (
                  <tr key={staffMember.staff_profile_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                          {staffMember.profile_picture_url ? (
                            <img 
                              src={staffMember.profile_picture_url} 
                              alt={staffMember.full_name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <User className="w-5 h-5 text-slate-500" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{staffMember.full_name}</div>
                          <div className="text-[15px] text-black font-bold">{staffMember.staff_code || `ID: ${staffMember.staff_profile_id}`}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Phone className="w-3 h-3" />
                          {formatMobileNumber(staffMember.mobile_number)}
                        </div>
                        <div className="text-sm text-slate-500">{staffMember.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {Array.isArray(staffMember.role) ? staffMember.role.map((r, index) => (
                          <span key={index} className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                            {r}
                          </span>
                        )) : (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                            {staffMember.role}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        staffMember.gender === 'MALE' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-pink-100 text-pink-800'
                      }`}>
                        {staffMember.gender}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {staffMember.willing_to_live_in ? (
                          <>
                            <Home className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-green-600">Yes</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 text-red-600" />
                            <span className="text-sm text-red-600">No</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(staffMember.current_status)}`}>
                        {getStatusIcon(staffMember.current_status)}
                        {staffMember.current_status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedStaff(staffMember)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="View Details"
                        >
                          <User className="w-4 h-4" />
                        </button>

                        {serviceRequest && (
                          sentCandidateIds.has(staffMember.staff_profile_id) ? (
                            <span
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg"
                              title="This profile has already been sent to the client"
                            >
                              <Check className="w-3.5 h-3.5" /> Sent
                            </span>
                          ) : (
                            <button
                              onClick={() => handleSendCandidate(staffMember)}
                              disabled={sendingCandidateId === staffMember.staff_profile_id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                              title="Send this candidate's profile to the client via WhatsApp"
                            >
                              {sendingCandidateId === staffMember.staff_profile_id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Send className="w-3.5 h-3.5" />}
                              Send to client
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Staff Details Modal */}
      {selectedStaff && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Staff Details</h2>
                <button
                  onClick={() => setSelectedStaff(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <XCircle className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
                  {selectedStaff.profile_picture_url ? (
                    <img 
                      src={selectedStaff.profile_picture_url} 
                      alt={selectedStaff.full_name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-8 h-8 text-slate-500" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{selectedStaff.full_name}</h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(selectedStaff.current_status)}`}>
                    {getStatusIcon(selectedStaff.current_status)}
                    {selectedStaff.current_status}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <p className="text-sm text-slate-900">{selectedStaff.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <p className="text-sm text-slate-900">{formatMobileNumber(selectedStaff.mobile_number)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Gender</label>
                  <p className="text-sm text-slate-900">{selectedStaff.gender}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Willing to Live-in</label>
                  <p className="text-sm text-slate-900">{selectedStaff.willing_to_live_in ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date of Birth</label>
                  <p className="text-sm text-slate-900">{formatDate(selectedStaff.date_of_birth)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Member Since</label>
                  <p className="text-sm text-slate-900">{formatDate(selectedStaff.created_at)}</p>
                </div>
              </div>
              
              {selectedStaff.qualifications && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Qualifications</label>
                  <p className="text-sm text-slate-900 whitespace-pre-wrap">{selectedStaff.qualifications}</p>
                </div>
              )}
              
              {selectedStaff.home_address && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <p className="text-sm text-slate-900">{selectedStaff.home_address}</p>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <Link to={`/admin/staff/${selectedStaff.staff_profile_id}/detail`} onClick={(e) => e.stopPropagation()}
                  className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg"
                >
                  Open full detail page
                </Link>
                <button
                  onClick={() => setSelectedStaff(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 backdrop-blur-sm bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border-solid border-[1px] border-black/20">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-900">Assign Staff to Service Request</h2>
                <button
                  onClick={resetAssignmentFlow}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              {/* Step Indicators */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {[
                    { step: 1, label: 'Select Agent' },
                    { step: 2, label: 'Upload Payment' },
                    { step: 3, label: 'Confirm Payment' }
                  ].map((item) => (
                    <div key={item.step} className="flex items-center">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                        assignmentStep >= item.step 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-slate-200 text-slate-600'
                      }`}>
                        {item.step}
                      </div>
                      <span className={`ml-2 text-sm font-medium ${
                        assignmentStep >= item.step ? 'text-blue-600' : 'text-slate-500'
                      }`}>
                        {item.label}
                      </span>
                      {item.step < 3 && (
                        <div className={`ml-4 w-8 h-0.5 ${
                          assignmentStep > item.step ? 'bg-blue-600' : 'bg-slate-200'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Step 1: Select Agent */}
              {assignmentStep === 1 && (
                <>
                  {/* Service Request Summary */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-4">Service Request Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-800">Patient: <PatientLink id={serviceRequest.patient_id}>{serviceRequest.patient_name}</PatientLink></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-800">Contact: {formatMobileNumber(serviceRequest.payer_mobile)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-800">Location: {serviceRequest.location_address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-800">Start: {new Date(serviceRequest.start_date).toLocaleDateString('en-GB')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-800">Service: {serviceRequest.service_type}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-800">Model: {serviceRequest.service_model?.replace('_', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-800">Age: {serviceRequest.patient_age}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-800">Condition: {serviceRequest.patient_condition}</span>
                      </div>
                    </div>
                    {serviceRequest.preferred_gender && serviceRequest.preferred_gender !== 'ANY' && (
                      <div className="mt-3 flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-800">Preferred Gender: {serviceRequest.preferred_gender}</span>
                      </div>
                    )}
                    {serviceRequest.remarks && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-blue-800 mb-1">Remarks:</p>
                        <p className="text-sm text-blue-700">{serviceRequest.remarks}</p>
                      </div>
                    )}
                  </div>

                  {/* Available Staff Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-900">
                        Available Staff Matching Criteria ({availableStaffForAssignment.length} found)
                      </h3>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-slate-600">Smart Filter:</label>
                        <button
                          onClick={() => setUseSmartFiltering(!useSmartFiltering)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            useSmartFiltering ? 'bg-blue-600' : 'bg-slate-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              useSmartFiltering ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                    
                    {!useSmartFiltering && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-amber-800">
                          <strong>Manual Selection Mode:</strong> Showing all available staff. Use filters above to narrow down options.
                        </p>
                      </div>
                    )}
                    {availableStaffForAssignment.length === 0 ? (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                        <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                        <p className="text-yellow-800">No available staff match the service request criteria</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {availableStaffForAssignment.map((staffMember) => (
                          <div key={staffMember.staff_profile_id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                                  {staffMember.profile_picture_url ? (
                                    <img 
                                      src={staffMember.profile_picture_url} 
                                      alt={staffMember.full_name}
                                      className="w-10 h-10 rounded-full object-cover"
                                    />
                                  ) : (
                                    <User className="w-5 h-5 text-slate-500" />
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium text-slate-900">{staffMember.full_name}{staffMember.staff_code ? <> (<strong className="font-bold">{staffMember.staff_code}</strong>)</> : ''}</div>
                                  <div className="text-sm text-slate-600">{formatMobileNumber(staffMember.mobile_number)}{staffMember.gender ? ` · ${staffMember.gender === 'MALE' ? 'Male' : staffMember.gender === 'FEMALE' ? 'Female' : staffMember.gender}` : ''}</div>
                                  <div className="text-sm text-slate-500">{staffMember.email}</div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(staffMember.current_status)}`}>
                                      {getStatusIcon(staffMember.current_status)}
                                      {staffMember.current_status}
                                    </span>
                                    {staffMember.willing_to_live_in && (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border-green-200">
                                        <Home className="w-3 h-3" />
                                        Live-in
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleConfirmAssignment(staffMember)}
                                disabled={assigningStaff}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {assigningStaff ? 'Assigning...' : 'Select'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Step 2: Upload Payment Slip */}
              {assignmentStep === 2 && (
                <div className="space-y-6">
                  {selectedStaffForAssignment && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h3 className="font-semibold text-green-900 mb-3">Selected Staff Member</h3>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                          {selectedStaffForAssignment.profile_picture_url ? (
                            <img 
                              src={selectedStaffForAssignment.profile_picture_url} 
                              alt={selectedStaffForAssignment.full_name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <User className="w-6 h-6 text-green-600" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-green-900">{selectedStaffForAssignment.full_name}</div>
                          <div className="text-sm text-green-700">{formatMobileNumber(selectedStaffForAssignment.mobile_number)}</div>
                          <div className="text-sm text-green-600">{selectedStaffForAssignment.email}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">Upload Payment Slip</h3>
                    {!paymentSlipFile ? (
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                        <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                        <label className="block cursor-pointer hover:bg-slate-50 rounded-lg p-4 -m-4 transition-colors">
                          <span className="text-slate-700 font-medium">Click to upload or drag and drop</span>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => setPaymentSlipFile(e.target.files[0])}
                            className="hidden"
                          />
                        </label>
                        <p className="text-sm text-slate-500 mt-2">
                          PNG, JPG, PDF up to 5MB
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-blue-900 mb-1">Selected file:</p>
                              <p className="text-sm text-blue-800 mb-1">{paymentSlipFile.name}</p>
                              <p className="text-xs text-blue-600">
                                Size: {(paymentSlipFile.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <button
                                type="button"
                                onClick={() => document.getElementById('payment-slip-input').click()}
                                className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 hover:bg-blue-200 rounded transition-colors"
                              >
                                Replace
                              </button>
                              <button
                                type="button"
                                onClick={() => setPaymentSlipFile(null)}
                                className="px-3 py-1 text-xs font-medium text-red-600 bg-red-100 hover:bg-red-200 rounded transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                        <input
                          id="payment-slip-input"
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setPaymentSlipFile(e.target.files[0])}
                          className="hidden"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Confirm Payment */}
              {assignmentStep === 3 && (
                <div className="space-y-6">
                  {/* Assignment Summary Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold">3</span>
                        </div>
                        Assignment Summary
                      </h3>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6 space-y-6">
                      {/* Service Request Section */}
                      <div className="bg-white rounded-lg p-4 border border-blue-100">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-3 h-3 text-blue-600" />
                          </div>
                          <h4 className="text-sm font-semibold text-blue-900">Service Request</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Care Profile:</span>
                            <span className="text-sm font-medium text-slate-900"><PatientLink id={serviceRequest.patient_id}>{serviceRequest.patient_name}</PatientLink></span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Service:</span>
                            <span className="text-sm font-medium text-slate-900">{serviceRequest.service_type}</span>
                          </div>
                          <div className="flex items-center gap-2 md:col-span-2">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            <span className="text-sm text-slate-700">{serviceRequest.location_address}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span className="text-sm text-slate-700">{new Date(serviceRequest.start_date).toLocaleDateString('en-GB')}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Assigned Staff Section */}
                      <div className="bg-white rounded-lg p-4 border border-green-100">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-3 h-3 text-green-600" />
                          </div>
                          <h4 className="text-sm font-semibold text-green-900">Assigned Staff</h4>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            {selectedStaffForAssignment.profile_picture_url ? (
                              <img 
                                src={selectedStaffForAssignment.profile_picture_url} 
                                alt={selectedStaffForAssignment.full_name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <User className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-900">{selectedStaffForAssignment.full_name}</p>
                            <div className="flex items-center gap-4 text-xs text-slate-600">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {formatMobileNumber(selectedStaffForAssignment.mobile_number)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {selectedStaffForAssignment.email}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Payment Slip Section */}
                      <div className="bg-white rounded-lg p-4 border border-amber-100">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                            <FileText className="w-3 h-3 text-amber-600" />
                          </div>
                          <h4 className="text-sm font-semibold text-amber-900">Payment Slip</h4>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-900">{paymentSlipFile.name}</p>
                            <p className="text-xs text-slate-600">
                              Size: {(paymentSlipFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Warning Notice */}
                  <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-900 mb-1">Final Confirmation Required</p>
                        <p className="text-sm text-amber-800">
                          Please review all details carefully. Once confirmed, this will create the booking and assign the staff member. The staff member will be notified immediately.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {bookingCompleted && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Booking Completed Successfully!</p>
                      <p className="text-sm text-green-700 mt-1">
                        {selectedStaffForAssignment?.full_name} has been assigned and will be notified. Redirecting to service requests...
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              {!bookingCompleted && (
                <div className="flex justify-between pt-4 border-t border-slate-200">
                <div>
                  {assignmentStep > 1 && (
                    <button
                      onClick={handleStepBack}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                      Back
                    </button>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={resetAssignmentFlow}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  
                  {assignmentStep === 1 && (
                    <button
                      disabled={availableStaffForAssignment.length === 0}
                      className="px-4 py-2 text-sm font-medium text-white bg-slate-400 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Select Staff Member to Continue
                    </button>
                  )}
                  
                  {assignmentStep === 2 && (
                    <button
                      onClick={handlePaymentNext}
                      disabled={!paymentSlipFile}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  )}
                  
                  {assignmentStep === 3 && (
                    <button
                      onClick={handlePaymentSubmit}
                      disabled={uploadingPayment}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {uploadingPayment ? 'Processing...' : 'Confirm Assignment'}
                    </button>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default StaffRoster;
